import { createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { normalizeSupplementalSizes } from '../../../../utilities/fileUploadService';

const applicationSlice = createSlice({
    name: 'application',
    initialState: {
        originalApplicationValues: {},
        codes: {}
    },
    reducers: {
        setOriginalApplicationValues: (state, action) => {
            state.originalApplicationValues = action.payload;                     
        },
        setApplication: (state, action) => {
            const incoming = cloneDeep(action.payload) || {};
            const existingSupplemental = state.supplementalItems || {};
            const incomingSupplemental = incoming.supplementalItems;

            if (Array.isArray(incomingSupplemental)) {
                incoming.supplementalItemsChecklist = incomingSupplemental;
                incoming.supplementalItems = { ...existingSupplemental };
            } else {
                const incomingObj = incomingSupplemental || {};
                // Merge per-question: incoming (server) data wins for each question,
                // but preserve local-only questions (e.g. in-progress uploads)
                const merged = { ...existingSupplemental };
                Object.keys(incomingObj).forEach(questionId => {
                    const incomingEntry = incomingObj[questionId];
                    const existingEntry = merged[questionId];
                    if (existingEntry && incomingEntry) {
                        // Server data wins, but preserve local-only fields (e.g. progress, uploading status)
                        merged[questionId] = {
                            ...existingEntry,
                            ...incomingEntry,
                            files: incomingEntry.files || existingEntry.files
                        };
                    } else {
                        merged[questionId] = incomingEntry;
                    }
                });
                incoming.supplementalItems = merged;
            }

            normalizeSupplementalSizes(incoming.supplementalItems);

            return incoming;
        },
        setApplicationPageCompletedStatus: (state, action) => {
            const { pageId, complete } = action.payload;
            if (state.pageStatuses[pageId] !== complete) {
                state.pageStatuses[pageId] = complete;
            }
        },
        setApplicationValue: (state, action) => {
            const name = action.payload.name;
            const value = action.payload.value;
            if (typeof name === 'string' && name.startsWith('supplementalItems.')) {
                const dotIndex = name.indexOf('.');
                const rest = name.substring(dotIndex + 1);
                // Create a new supplementalItems object so React detects the reference change
                state.supplementalItems = {
                    ...state.supplementalItems,
                    [rest]: value
                };
            } else {
                set(state, name, value);
            }
        },
        setStatus: (state, action) => {
            state.status = action.payload;
        },
        setCode: (state, action) => {
            const { path, code } = action.payload;
            state.codes = state.codes || {};
            set(state.codes, path, code);

            return state;
        },
        setCurriculumRules: (state, action) => {
            state.curriculumRules = action.payload;

            return state;
        },
        setCurriculumValues: (state, action) => {
            state.curriculumValues = action.payload;

            return state;
        }
        ,
        setCurriculumLoading: (state, action) => {
            state.curriculumLoading = action.payload;
        },
        deleteApplicationValue: (state, action) => {
            const name = action.payload;
            if (typeof name === 'string' && name.startsWith('supplementalItems.')) {
                const dotIndex = name.indexOf('.');
                const rest = name.substring(dotIndex + 1);
                // Create a new supplementalItems object so React detects the reference change
                const updated = { ...state.supplementalItems };
                delete updated[rest];
                state.supplementalItems = updated;
            } else {
                unset(state, name);
            }
            return state;
        }
    },
});
export const selectCurriculumValues = state => state.application?.curriculumValues;
export const selectCurriculumLoading = state => !!state.application?.curriculumLoading;
export const selectApplicationStatus = state => state.application?.status;
export const selectApplicationIsIncomplete = state => state.application?.status === 'INCOMPLETE';
export const selectSupplementalItems = state => state.application?.supplementalItems;
export const {
    setApplication,
    setApplicationPageCompletedStatus,
    setApplicationValue,
    setOriginalApplicationValues,    
    setStatus,
    setCode,
    setCurriculumRules,
    setCurriculumValues,
    setCurriculumLoading,
    deleteApplicationValue
} = applicationSlice.actions;
export default applicationSlice.reducer;
